/**
 * Bhāva cusps
 *
 * Lagna (ascendant), MC, and the twelve cusps under several systems:
 *
 *   wholeSign  — the lagna's sign IS bhāva 1; the default, and what most of
 *                classical Jyotiṣa assumes.
 *                ⚠ **In this module that means a TROPICAL sign, which is not a
 *                rāśi.** The snap to a multiple of 30° happens in the tropical
 *                frame, so converting these twelve cusps to sidereal afterwards
 *                subtracts the ayanāṁśa from each and leaves bhāva 1 beginning
 *                ~24° *into* a rāśi. The result still tiles the circle and still
 *                puts every graha in exactly one bhāva, so nothing looks wrong —
 *                it is simply off by a house for most positions.
 *                ◆ Whole-sign rāśi cusps are therefore built in the Jyotiṣa
 *                layer instead: `wholeSignCusps()` in src/jyotish/bhava.js. This
 *                branch is kept because it is the correct tropical answer and
 *                the equal/Śrīpati/Placidus branches genuinely do convert.
 *   equal      — 30° from the lagna degree
 *   sripati    — Porphyry-style trisection of the quadrants, the usual Indian
 *                'bhāva cakra'
 *   placidus   — time-based, used by KP practitioners
 *   kp         — Placidus cusps read in the sidereal zodiac
 *
 * Fails loudly above |lat| 66° instead of returning nonsense for Placidus.
 *
 * © Manish Jagdish Thatte
 */

import { ramc, trueObliquity } from './sidereal-time.js';

const D2R = Math.PI / 180, R2D = 180 / Math.PI;
const norm360 = (d) => ((d % 360) + 360) % 360;
const sin = (d) => Math.sin(d * D2R);
const cos = (d) => Math.cos(d * D2R);
const tan = (d) => Math.tan(d * D2R);

/** Above this |latitude| the Placidus construction has no solution. */
export const PLACIDUS_LIMIT_DEG = 66;

export const HOUSE_SYSTEMS = ['wholeSign', 'equal', 'sripati', 'placidus', 'kp'];

/**
 * The lagna — tropical ecliptic longitude of the eastern horizon, degrees.
 *
 *     λ_asc = atan2( cos θ , −( sin θ · cos ε + tan φ · sin ε ) )     θ = RAMC
 *
 * ⚠ **Sign note, and it cost a re-derivation.** The plan (and the old
 * `KUNDLI.del` it was taken from) records this as
 * `atan2(−cos θ, sin θ·cos ε + tan φ·sin ε)`. Negating *both* arguments of
 * atan2 rotates the result by exactly 180°, so that form returns the
 * **descendant**. The check that settles it is in the test suite and needs no
 * outside chart: convert the returned longitude back to horizontal coordinates
 * and confirm it is on the horizon *and* in the eastern half of the sky. The
 * form above passes; the recorded one is 180° out.
 */
export function ascendantFromRAMC(ramcDeg, latDeg, oblDeg) {
  requireLatitude(latDeg);
  const theta = ramcDeg;
  return norm360(
    Math.atan2(cos(theta), -(sin(theta) * cos(oblDeg) + tan(latDeg) * sin(oblDeg))) * R2D
  );
}

/** The lagna for an instant and a place. `lonEast` is positive east. */
export function ascendant(jdUT, latDeg, lonEast) {
  return ascendantFromRAMC(ramc(jdUT, lonEast), latDeg, trueObliquity(jdUT));
}

/**
 * The midheaven — tropical longitude of the ecliptic point on the meridian.
 *
 *     λ_mc = atan2( sin θ , cos θ · cos ε )
 *
 * Independent of latitude, which is a useful smoke test in itself.
 */
export function midheavenFromRAMC(ramcDeg, oblDeg) {
  return norm360(Math.atan2(sin(ramcDeg), cos(ramcDeg) * cos(oblDeg)) * R2D);
}

/** The midheaven for an instant and a place. */
export function midheaven(jdUT, lonEast) {
  return midheavenFromRAMC(ramc(jdUT, lonEast), trueObliquity(jdUT));
}

/**
 * The twelve bhāva cusps, tropical, in degrees, index 0 = bhāva 1.
 *
 * ◆ Whole-sign is the default because that is what the classical texts assume
 * when they say "the fifth from the lagna".
 *
 * ⚠ These are **tropical**. The sidereal reading happens exactly once, in
 * ayanamsa.js, downstream of here. That is also why `kp` and `placidus` return
 * the same numbers at this layer: KP *is* Placidus, read in the sidereal
 * zodiac, and the reading is not this module's job.
 *
 * ⚠ **`wholeSign` is the one system for which "convert the cusps afterwards" is
 * wrong**, because it is defined by which *sign* a point falls in rather than by
 * an angle. See the header note and src/jyotish/bhava.js § wholeSignCusps.
 */
export function houseCusps(jdUT, latDeg, lonEast, system = 'wholeSign') {
  const theta = ramc(jdUT, lonEast);
  const obl = trueObliquity(jdUT);
  return cuspsFrom(theta, latDeg, obl, system);
}

/** The same construction from raw angles, which is what the tests drive. */
export function cuspsFrom(ramcDeg, latDeg, oblDeg, system = 'wholeSign') {
  requireLatitude(latDeg);
  const asc = ascendantFromRAMC(ramcDeg, latDeg, oblDeg);
  const mc = midheavenFromRAMC(ramcDeg, oblDeg);

  switch (system) {
    case 'wholeSign': {
      const start = Math.floor(asc / 30) * 30;
      return Array.from({ length: 12 }, (_, i) => norm360(start + i * 30));
    }
    case 'equal':
      return Array.from({ length: 12 }, (_, i) => norm360(asc + i * 30));
    case 'sripati':
      return porphyry(asc, mc);
    case 'placidus':
    case 'kp':
      return placidus(ramcDeg, latDeg, oblDeg, asc, mc);
    default:
      throw new RangeError(
        `unknown house system "${system}" — expected one of ${HOUSE_SYSTEMS.join(', ')}`
      );
  }
}

/**
 * Porphyry / Śrīpati: trisect each quadrant between the four angles.
 *
 * ⚠ In the Indian 'bhāva cakra' these twelve points are traditionally the
 * bhāva **madhya** (midpoints), not the boundaries. `sripatiBhavaMadhya` below
 * returns them in that reading so neither convention has to be guessed at.
 */
function porphyry(asc, mc) {
  const ic = norm360(mc + 180);
  const desc = norm360(asc + 180);
  const cusps = new Array(12);

  const arcMcToAsc = norm360(asc - mc);
  const arcAscToIc = norm360(ic - asc);

  cusps[0] = asc;                                  // 1
  cusps[1] = norm360(asc + arcAscToIc / 3);        // 2
  cusps[2] = norm360(asc + (2 * arcAscToIc) / 3);  // 3
  cusps[3] = ic;                                   // 4
  cusps[9] = mc;                                   // 10
  cusps[10] = norm360(mc + arcMcToAsc / 3);        // 11
  cusps[11] = norm360(mc + (2 * arcMcToAsc) / 3);  // 12
  cusps[6] = desc;                                 // 7
  for (const i of [4, 5, 7, 8]) cusps[i] = norm360(cusps[(i + 6) % 12] + 180);
  return cusps;
}

/** The Śrīpati cusps read as bhāva midpoints, with boundaries halfway between. */
export function sripatiBhavaMadhya(jdUT, latDeg, lonEast) {
  const madhya = houseCusps(jdUT, latDeg, lonEast, 'sripati');
  const bounds = madhya.map((m, i) => {
    const prev = madhya[(i + 11) % 12];
    return norm360(prev + norm360(m - prev) / 2);
  });
  return { madhya, bounds };
}

/**
 * Placidus: divide each point's semi-arc by time rather than the ecliptic by
 * space. Cusps 11, 12, 2 and 3 are found by iteration; the rest are the angles
 * and the oppositions.
 *
 * ⚠ **Throws above |lat| 66°.** Near the poles the required point never reaches
 * the fraction of its semi-arc the construction asks for — the arcsine goes out
 * of domain — and the honest answer is that the system has no cusp there. It
 * must not return NaN and it must not quietly become equal houses: a KP
 * practitioner reading equal-house cusps labelled Placidus gets wrong answers
 * with no sign anything went wrong.
 */
function placidus(ramcDeg, latDeg, oblDeg, asc, mc) {
  if (Math.abs(latDeg) > PLACIDUS_LIMIT_DEG) {
    throw new RangeError(
      `Placidus house cusps are undefined above |latitude| ${PLACIDUS_LIMIT_DEG}° ` +
      `(got ${latDeg.toFixed(4)}°). Choose whole-sign, equal or Śrīpati instead.`
    );
  }

  const cusps = new Array(12);
  cusps[0] = asc;
  cusps[9] = mc;
  cusps[10] = placidusCusp(ramcDeg, latDeg, oblDeg, 11);
  cusps[11] = placidusCusp(ramcDeg, latDeg, oblDeg, 12);
  cusps[1] = placidusCusp(ramcDeg, latDeg, oblDeg, 2);
  cusps[2] = placidusCusp(ramcDeg, latDeg, oblDeg, 3);
  for (const i of [3, 4, 5, 6, 7, 8]) cusps[i] = norm360(cusps[(i + 6) % 12] + 180);
  return cusps;
}

/**
 * One intermediate Placidus cusp, by fixed-point iteration on its right
 * ascension. Converges in a handful of passes away from the polar limit.
 */
function placidusCusp(ramcDeg, latDeg, oblDeg, house) {
  const spec = {
    11: { seed: 30, f: 1 / 3, nocturnal: false },
    12: { seed: 60, f: 2 / 3, nocturnal: false },
    2: { seed: 120, f: 2 / 3, nocturnal: true },
    3: { seed: 150, f: 1 / 3, nocturnal: true }
  }[house];

  let ra = norm360(ramcDeg + spec.seed);
  for (let i = 0; i < 60; i++) {
    // Declination of the ecliptic point at this right ascension.
    const dec = Math.atan(sin(ra) * tan(oblDeg)) * R2D;
    // Ascensional difference: how far the horizon crossing is displaced from
    // the 6-hour circle at this declination and latitude.
    const s = tan(latDeg) * tan(dec);
    if (Math.abs(s) > 1) {
      throw new RangeError(
        `Placidus cusp ${house} is undefined at latitude ${latDeg.toFixed(4)}°: ` +
        'the point is circumpolar and never crosses the horizon.'
      );
    }
    const ad = Math.asin(s) * R2D;

    const next = spec.nocturnal
      ? norm360(ramcDeg + 180 - spec.f * (90 - ad))
      : norm360(ramcDeg + spec.f * (90 + ad));

    let delta = next - ra;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    ra = norm360(ra + delta);
    if (Math.abs(delta) < 1e-10) break;
  }

  // Right ascension → tropical ecliptic longitude, same quadrant.
  return norm360(Math.atan2(sin(ra), cos(ra) * cos(oblDeg)) * R2D);
}

/**
 * Which bhāva a longitude falls in. 1-based.
 *
 * ◆ A longitude exactly on a boundary belongs to the **later** division —
 * the project-wide rule, applied here so that whole-sign bhāvas and rāśis agree
 * about what happens at 0°00′00″ of a sign.
 */
export function houseOf(lon, cusps) {
  if (!Array.isArray(cusps) || cusps.length !== 12) {
    throw new TypeError('houseOf: cusps must be an array of 12 longitudes');
  }
  const L = norm360(lon);
  for (let i = 0; i < 12; i++) {
    const start = norm360(cusps[i]);
    const span = norm360(norm360(cusps[(i + 1) % 12]) - start);
    const offset = norm360(L - start);
    // `offset < span` with offset==0 included puts a boundary longitude in the
    // house that starts there, which is the later division.
    if (offset < span || (span === 0 && offset === 0)) return i + 1;
  }
  return 12;
}

function requireLatitude(latDeg) {
  if (!Number.isFinite(latDeg)) throw new TypeError('latitude must be finite');
  if (latDeg < -90 || latDeg > 90) throw new RangeError(`latitude ${latDeg} out of range −90…90`);
  if (Math.abs(Math.abs(latDeg) - 90) < 1e-9) {
    throw new RangeError('latitude ±90° has no ascendant — the horizon is the ecliptic pole');
  }
}
